/**
 * React Query hooks for admin cost tracking, jobs, and logs data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

// =============================================================================
// Query Keys
// =============================================================================

// Cost query keys
export const adminCostKeys = {
  all: ['admin', 'costs'] as const,
  summary: (days: number) => [...adminCostKeys.all, 'summary', days] as const,
  byCompany: (days: number, limit: number) =>
    [...adminCostKeys.all, 'by-company', days, limit] as const,
  byProvider: (days: number) => [...adminCostKeys.all, 'by-provider', days] as const,
  buildHistory: (companyId?: string, buildType?: string, limit?: number) =>
    [...adminCostKeys.all, 'build-history', companyId, buildType, limit] as const,
  projection: (months: number) => [...adminCostKeys.all, 'projection', months] as const,
};

// Jobs query keys
export const adminJobKeys = {
  all: ['admin', 'jobs'] as const,
  active: (limit: number) => [...adminJobKeys.all, 'active', limit] as const,
  recent: (limit: number, status?: string) =>
    [...adminJobKeys.all, 'recent', limit, status] as const,
  scheduled: () => [...adminJobKeys.all, 'scheduled'] as const,
  stats: () => [...adminJobKeys.all, 'stats'] as const,
};

// Logs query keys
export const adminLogKeys = {
  all: ['admin', 'logs'] as const,
  exceptions: (limit: number, errorCode?: string) =>
    [...adminLogKeys.all, 'exceptions', limit, errorCode] as const,
  stats: () => [...adminLogKeys.all, 'stats'] as const,
  errorCodes: () => [...adminLogKeys.all, 'error-codes'] as const,
};

/**
 * Get aggregated cost summary for a period
 */
export function useAdminCostSummary(days: number = 30) {
  return useQuery({
    queryKey: adminCostKeys.summary(days),
    queryFn: () => api.getAdminCostSummary(days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get per-company cost breakdown
 */
export function useAdminCostsByCompany(days: number = 30, limit: number = 20) {
  return useQuery({
    queryKey: adminCostKeys.byCompany(days, limit),
    queryFn: () => api.getAdminCostsByCompany(days, limit),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get per-provider cost breakdown
 */
export function useAdminCostsByProvider(days: number = 30) {
  return useQuery({
    queryKey: adminCostKeys.byProvider(days),
    queryFn: () => api.getAdminCostsByProvider(days),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get KB build history
 */
export function useAdminBuildHistory(
  companyId?: string,
  buildType?: string,
  limit: number = 50
) {
  return useQuery({
    queryKey: adminCostKeys.buildHistory(companyId, buildType, limit),
    queryFn: () => api.getAdminBuildHistory(companyId, buildType, limit),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get cost projection
 */
export function useAdminCostProjection(months: number = 3) {
  return useQuery({
    queryKey: adminCostKeys.projection(months),
    queryFn: () => api.getAdminCostProjection(months),
    staleTime: 10 * 60 * 1000, // 10 minutes (projections don't change frequently)
  });
}

// =============================================================================
// Jobs Hooks
// =============================================================================

/**
 * Get active (running + queued + pending) jobs
 */
export function useAdminActiveJobs(limit: number = 50, refetchInterval?: number) {
  return useQuery({
    queryKey: adminJobKeys.active(limit),
    queryFn: () => api.getAdminActiveJobs(limit),
    staleTime: 5 * 1000, // 5 seconds - jobs change frequently
    refetchInterval: refetchInterval ?? 10 * 1000, // Auto-refresh every 10 seconds
  });
}

/**
 * Get recent jobs (all statuses)
 */
export function useAdminRecentJobs(
  limit: number = 50,
  status?: string,
  refetchInterval?: number
) {
  return useQuery({
    queryKey: adminJobKeys.recent(limit, status),
    queryFn: () => api.getAdminRecentJobs(limit, status),
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: refetchInterval ?? 30 * 1000, // Auto-refresh every 30 seconds
  });
}

/**
 * Get scheduled recurring jobs from APScheduler
 */
export function useAdminScheduledJobs() {
  return useQuery({
    queryKey: adminJobKeys.scheduled(),
    queryFn: () => api.getAdminScheduledJobs(),
    staleTime: 60 * 1000, // 1 minute - scheduled jobs don't change often
  });
}

/**
 * Get job queue statistics
 */
export function useAdminJobStats(refetchInterval?: number) {
  return useQuery({
    queryKey: adminJobKeys.stats(),
    queryFn: () => api.getAdminJobStats(),
    staleTime: 5 * 1000, // 5 seconds
    refetchInterval: refetchInterval ?? 10 * 1000, // Auto-refresh every 10 seconds
  });
}

/**
 * Cancel a job mutation
 */
export function useCancelJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => api.cancelAdminJob(jobId),
    onSuccess: () => {
      // Invalidate all job queries to refresh the data
      queryClient.invalidateQueries({ queryKey: adminJobKeys.all });
    },
  });
}

// =============================================================================
// Logs Hooks
// =============================================================================

/**
 * Get recent exception logs
 */
export function useAdminExceptionLogs(
  limit: number = 100,
  errorCode?: string,
  refetchInterval?: number
) {
  return useQuery({
    queryKey: adminLogKeys.exceptions(limit, errorCode),
    queryFn: () => api.getAdminExceptionLogs(limit, errorCode),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: refetchInterval ?? 60 * 1000, // Auto-refresh every minute
  });
}

/**
 * Get exception log statistics
 */
export function useAdminLogStats(refetchInterval?: number) {
  return useQuery({
    queryKey: adminLogKeys.stats(),
    queryFn: () => api.getAdminLogStats(),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: refetchInterval ?? 60 * 1000, // Auto-refresh every minute
  });
}

/**
 * Get list of unique error codes for filtering
 */
export function useAdminErrorCodes() {
  return useQuery({
    queryKey: adminLogKeys.errorCodes(),
    queryFn: () => api.getAdminErrorCodes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Build Progress Hooks
// =============================================================================

// Build progress query keys
export const adminBuildProgressKeys = {
  all: ['admin', 'builds', 'progress'] as const,
  active: () => [...adminBuildProgressKeys.all, 'active'] as const,
  company: (companyId: string) => [...adminBuildProgressKeys.all, 'company', companyId] as const,
  history: (companyId: string, limit: number) =>
    [...adminBuildProgressKeys.all, 'history', companyId, limit] as const,
};

/**
 * Get all active build progress
 */
export function useAdminBuildProgress(refetchInterval: number = 3000) {
  return useQuery({
    queryKey: adminBuildProgressKeys.active(),
    queryFn: () => api.getAdminBuildProgress(),
    staleTime: 1000, // 1 second
    refetchInterval, // Auto-refresh every 3 seconds by default
  });
}

/**
 * Get build progress for a specific company
 */
export function useAdminCompanyBuildProgress(
  companyId: string,
  enabled: boolean = true,
  refetchInterval: number = 3000
) {
  return useQuery({
    queryKey: adminBuildProgressKeys.company(companyId),
    queryFn: () => api.getAdminCompanyBuildProgress(companyId),
    enabled,
    staleTime: 1000,
    refetchInterval,
  });
}

/**
 * Get phase history for a company
 */
export function useAdminPhaseHistory(companyId: string, limit: number = 50) {
  return useQuery({
    queryKey: adminBuildProgressKeys.history(companyId, limit),
    queryFn: () => api.getAdminPhaseHistory(companyId, limit),
    staleTime: 30 * 1000, // 30 seconds
  });
}
